import React, { useEffect, useState } from 'react';
import { Paper, Box, Typography, Button, Divider } from '@mui/material';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useTranslation } from 'react-i18next';
import { IC, IFontSizes, IFontWeights, ILineHeight, IRadius } from '../insightsTheme';

// Session cache so the thinking/typing animation only plays once per content hash.
const _execPlayed = {};

/**
 * ExecSummary — the AI-native hero of the landing.
 *
 * Unlike the earlier implementation which hardcoded Spanish prose + highlights,
 * this version receives its narrative as `paragraphs` (already t()-resolved by the
 * orchestrator) and derives highlights dynamically from the pieces it's told to
 * emphasise (`highlights: string[]`).
 *
 * Props:
 *   thinkSteps: string[]            — already-translated "thinking…" lines
 *   paragraphs: string[]            — already-translated body lines (top to bottom)
 *   highlights: string[]            — substrings within paragraphs to emphasise
 *   title: string                   — already translated
 * Status labels read from i18n internally.
 */
const ExecSummary = ({ thinkSteps = [], paragraphs = [], highlights = [], title, ctas = [] }) => {
  const { t } = useTranslation();
  const fullText = paragraphs.join('\n');
  const cacheKey = `${title}::${fullText.slice(0, 80)}`;
  const skip = _execPlayed[cacheKey];

  const [phase, setPhase] = useState(skip ? 'done' : 'thinking');
  const [thinkIdx, setThinkIdx] = useState(0);
  const [fadeIn, setFadeIn] = useState(true);
  const [charCount, setCharCount] = useState(skip ? fullText.length : 0);

  // Thinking timer — isolated from content so progressive data loads (which change
  // fullText/cacheKey) cannot cancel and restart it, which used to wedge thinkIdx at 0.
  useEffect(() => {
    if (phase !== 'thinking') return undefined;
    if (thinkIdx >= thinkSteps.length) {
      setPhase('typing');
      return undefined;
    }
    const hold = thinkIdx < 3 ? 900 : thinkIdx < 6 ? 700 : 500;
    const t1 = setTimeout(() => {
      setFadeIn(false);
      setTimeout(() => {
        setThinkIdx((i) => i + 1);
        setFadeIn(true);
      }, 300);
    }, hold);
    return () => clearTimeout(t1);
  }, [phase, thinkIdx, thinkSteps.length]);

  // Typing timer — types toward the current fullText length (which may grow as data
  // arrives); marks done once caught up.
  useEffect(() => {
    if (phase !== 'typing') return undefined;
    if (charCount >= fullText.length) {
      setPhase('done');
      _execPlayed[cacheKey] = true;
      return undefined;
    }
    const t2 = setTimeout(() => setCharCount((c) => Math.min(c + 2, fullText.length)), 22);
    return () => clearTimeout(t2);
  }, [phase, charCount, fullText.length, cacheKey]);

  const highlightParts = (text) => {
    if (!highlights.length) return text;
    let parts = [{ text, emphasised: false }];
    for (const hl of highlights) {
      if (!hl) continue;
      const next = [];
      for (const part of parts) {
        if (part.emphasised) {
          next.push(part);
          continue;
        }
        const idx = part.text.indexOf(hl);
        if (idx === -1) {
          next.push(part);
          continue;
        }
        if (idx > 0) next.push({ text: part.text.slice(0, idx), emphasised: false });
        next.push({ text: hl, emphasised: true });
        if (idx + hl.length < part.text.length) {
          next.push({ text: part.text.slice(idx + hl.length), emphasised: false });
        }
      }
      parts = next;
    }
    return parts.map((p, i) =>
      p.emphasised ? (
        <Box key={i} component="span" sx={{ fontWeight: IFontWeights.strong, color: IC.ai }}>
          {p.text}
        </Box>
      ) : (
        <React.Fragment key={i}>{p.text}</React.Fragment>
      )
    );
  };

  const isActive = phase !== 'done';
  const currentStep = thinkIdx < thinkSteps.length ? thinkSteps[thinkIdx] : '';

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: `${IRadius.card}px`,
        borderColor: IC.bdr,
        borderLeft: `3px solid ${IC.ai}`,
        p: 2.5,
        height: '100%',
        minHeight: 180,
        display: 'flex',
        flexDirection: 'column',
        background: IC.surface,
      }}
    >
      <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: '8px',
            background: IC.aiSoft,
            color: IC.ai,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            mt: 0.25,
            animation: isActive ? 'exec-badge-pulse 1.6s ease-in-out infinite' : 'none',
          }}
        >
          <AutoAwesomeOutlinedIcon sx={{ fontSize: 16, color: IC.ai }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
            <Typography
              component="span"
              sx={{ fontSize: `${IFontSizes.sm}px`, fontWeight: IFontWeights.strong, color: IC.pri }}
            >
              {title}
            </Typography>
            {phase === 'thinking' && (
              <Typography
                component="span"
                sx={{ fontSize: `${IFontSizes.xs}px`, color: IC.txM, fontStyle: 'italic' }}
              >
                {t('insights_v2_exec_thinking')}
              </Typography>
            )}
            {phase === 'typing' && (
              <Typography
                component="span"
                sx={{ fontSize: `${IFontSizes.xs}px`, color: IC.ai, fontWeight: IFontWeights.label }}
              >
                {t('insights_v2_exec_writing')}
              </Typography>
            )}
            {phase === 'done' && (
              <Typography
                component="span"
                sx={{ fontSize: `${IFontSizes.xs}px`, color: IC.grn, fontWeight: IFontWeights.label }}
              >
                ✓ {t('insights_v2_exec_complete')}
              </Typography>
            )}
          </Box>
          <Box sx={{ minHeight: 200 }}>
            {phase === 'thinking' && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  height: 22,
                  opacity: fadeIn ? 1 : 0,
                  transition: 'opacity 0.25s ease',
                }}
              >
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    background: IC.ai,
                    animation: 'exec-badge-pulse 0.8s ease-in-out infinite',
                    flexShrink: 0,
                  }}
                />
                <Typography
                  component="span"
                  sx={{ fontSize: `${IFontSizes.sm}px`, color: IC.ai, fontWeight: IFontWeights.label }}
                >
                  {currentStep}
                </Typography>
              </Box>
            )}
            {(phase === 'typing' || phase === 'done') && (
              <Typography
                component="div"
                sx={{
                  fontSize: `${IFontSizes.md}px`,
                  color: IC.tx2,
                  lineHeight: ILineHeight.prose,
                  fontWeight: IFontWeights.body,
                }}
              >
                {(() => {
                  const shown = phase === 'done' ? fullText : fullText.slice(0, charCount);
                  const lines = shown.split('\n');
                  const [headline, ...bullets] = lines;
                  return (
                    <>
                      {headline !== undefined && (
                        <Box sx={{ mb: bullets.length > 0 ? 1 : 0 }}>
                          {highlightParts(headline)}
                        </Box>
                      )}
                      {bullets.length > 0 && (
                        <Box
                          component="ul"
                          sx={{
                            m: 0,
                            pl: '18px',
                            '& li': { mb: 0.5 },
                            '& li:last-of-type': { mb: 0 },
                          }}
                        >
                          {bullets.map((line, pi) => (
                            <Box component="li" key={pi}>
                              {highlightParts(line)}
                            </Box>
                          ))}
                        </Box>
                      )}
                    </>
                  );
                })()}
                {phase === 'typing' && (
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-block',
                      width: 2,
                      height: 14,
                      background: IC.ai,
                      ml: '1px',
                      animation: 'exec-cursor 0.6s step-end infinite',
                      verticalAlign: 'text-bottom',
                    }}
                  />
                )}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
      {phase === 'done' && ctas.length > 0 && (
        <>
          <Divider sx={{ my: 1.5, borderColor: IC.bdrL }} />
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {ctas.map((cta, i) => (
              <Button
                key={i}
                size="small"
                variant="text"
                onClick={cta.onClick}
                endIcon={<ArrowForwardIcon sx={{ fontSize: 14 }} />}
                sx={{
                  fontSize: `${IFontSizes.xs}px`,
                  fontWeight: IFontWeights.label,
                  color: IC.ai,
                  textTransform: 'none',
                  px: 1,
                  py: 0.25,
                  '&:hover': { background: IC.aiSoft },
                }}
              >
                {cta.label}
              </Button>
            ))}
          </Box>
        </>
      )}
      <style>{`
        @keyframes exec-badge-pulse{0%,100%{transform:scale(1);opacity:1}50%{transform:scale(1.08);opacity:0.72}}
        @keyframes exec-cursor{0%,100%{opacity:1}50%{opacity:0}}
      `}</style>
    </Paper>
  );
};

export default ExecSummary;
